/**
 * Google Analytics 4 integration with Bulgarian custom events
 * Tracks user interactions throughout the GulArt shop
 */

// Types for GA4
declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    dataLayer: unknown[];
  }
}

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_ID;

/**
 * Initialize Google Analytics
 */
export function initGA() {
  if (typeof window === 'undefined') return;
  if (!GA_MEASUREMENT_ID) return;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer.push(args);
  };

  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    // Bulgarian locale settings
    language: 'bg',
    country: 'BG',
    currency: 'EUR',
    // Privacy settings
    anonymize_ip: true,
    // Page view tracking
    send_page_view: true,
  });
}

/**
 * Initialize GA after the page is fully loaded and the browser is idle.
 * Keeps GTM's ~135 KB transfer and main-thread work out of the LCP window.
 * Falls back to a timer if requestIdleCallback isn't available (Safari).
 */
export function initGADeferred() {
  if (typeof window === 'undefined') return;
  if (!GA_MEASUREMENT_ID) return;

  const schedule = () => {
    const w = window as Window &
      typeof globalThis & {
        requestIdleCallback?: (
          cb: () => void,
          opts?: { timeout: number }
        ) => number;
      };
    if (typeof w.requestIdleCallback === 'function') {
      w.requestIdleCallback(() => initGA(), { timeout: 3000 });
    } else {
      setTimeout(() => initGA(), 1500);
    }
  };

  if (document.readyState === 'complete') {
    schedule();
  } else {
    window.addEventListener('load', schedule, { once: true });
  }
}

/**
 * Track page views
 */
export function trackPageView(url: string, title: string) {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('event', 'page_view', {
    page_path: url,
    page_title: title,
    page_location: window.location.href,
  });
}

/**
 * Bulgarian event names mapping
 */
const EVENT_NAMES = {
  // Product events
  VIEW_PRODUCT: 'преглед_продукт',
  ADD_TO_CART: 'добави_в_количка',
  REMOVE_FROM_CART: 'премахни_от_количка',
  VIEW_CART: 'преглед_количка',

  // Checkout events
  BEGIN_CHECKOUT: 'начало_поръчка',
  ADD_SHIPPING_INFO: 'добави_доставка',
  ADD_PAYMENT_INFO: 'добави_плащане',
  PURCHASE: 'покупка',

  // Designer events
  OPEN_DESIGNER: 'отвори_дизайнер',
  ADD_TEXT: 'добави_текст',
  UPLOAD_IMAGE: 'качи_изображение',
  SAVE_DESIGN: 'запази_дизайн',

  // Search events
  SEARCH: 'търсене',
  VIEW_CATEGORY: 'преглед_категория',

  // User events
  SIGN_UP: 'регистрация',
  LOGIN: 'вход',
  CONTACT_FORM: 'форма_контакт',

  // Engagement events
  SHARE: 'споделяне',
  CLICK_PHONE: 'клик_телефон',
  CLICK_EMAIL: 'клик_имейл',
};

/**
 * Track product view
 */
export function trackProductView(product: {
  id: string;
  name: string;
  category: string;
  price: number;
}) {
  if (typeof window === 'undefined' || !window.gtag) return;

  // GA4 standard event
  window.gtag('event', 'view_item', {
    currency: 'EUR',
    value: product.price,
    items: [
      {
        item_id: product.id,
        item_name: product.name,
        item_category: product.category,
        price: product.price,
        quantity: 1,
      },
    ],
  });

  // Custom Bulgarian event
  window.gtag('event', EVENT_NAMES.VIEW_PRODUCT, {
    product_id: product.id,
    product_name: product.name,
    product_category: product.category,
    product_price: product.price,
  });
}

/**
 * Track add to cart
 */
export function trackAddToCart(item: {
  id: string;
  name: string;
  category: string;
  price: number;
  quantity: number;
  designPrice?: number;
}) {
  if (typeof window === 'undefined' || !window.gtag) return;

  const totalPrice = (item.price + (item.designPrice || 0)) * item.quantity;

  // GA4 standard event
  window.gtag('event', 'add_to_cart', {
    currency: 'EUR',
    value: totalPrice,
    items: [
      {
        item_id: item.id,
        item_name: item.name,
        item_category: item.category,
        price: item.price,
        quantity: item.quantity,
      },
    ],
  });

  // Custom Bulgarian event
  window.gtag('event', EVENT_NAMES.ADD_TO_CART, {
    product_id: item.id,
    product_name: item.name,
    quantity: item.quantity,
    total_price: totalPrice,
    has_custom_design: !!item.designPrice,
  });
}

/**
 * Track remove from cart
 */
export function trackRemoveFromCart(item: {
  id: string;
  name: string;
  price: number;
  quantity: number;
}) {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('event', 'remove_from_cart', {
    currency: 'EUR',
    value: item.price * item.quantity,
    items: [
      {
        item_id: item.id,
        item_name: item.name,
        price: item.price,
        quantity: item.quantity,
      },
    ],
  });

  window.gtag('event', EVENT_NAMES.REMOVE_FROM_CART, {
    product_id: item.id,
    product_name: item.name,
  });
}

/**
 * Track begin checkout
 */
export function trackBeginCheckout(cart: {
  items: Array<{
    id: string;
    name: string;
    category: string;
    price: number;
    quantity: number;
  }>;
  total: number;
}) {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('event', 'begin_checkout', {
    currency: 'EUR',
    value: cart.total,
    items: cart.items.map((item) => ({
      item_id: item.id,
      item_name: item.name,
      item_category: item.category,
      price: item.price,
      quantity: item.quantity,
    })),
  });

  window.gtag('event', EVENT_NAMES.BEGIN_CHECKOUT, {
    cart_total: cart.total,
    items_count: cart.items.length,
  });
}

/**
 * Track add shipping info
 */
export function trackAddShippingInfo(data: {
  shippingMethod: string;
  city: string;
  total: number;
}) {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('event', 'add_shipping_info', {
    currency: 'EUR',
    value: data.total,
    shipping_tier: data.shippingMethod,
  });

  window.gtag('event', EVENT_NAMES.ADD_SHIPPING_INFO, {
    shipping_method: data.shippingMethod,
    city: data.city,
  });
}

/**
 * Track add payment info
 */
export function trackAddPaymentInfo(data: {
  paymentMethod: string;
  total: number;
}) {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('event', 'add_payment_info', {
    currency: 'EUR',
    value: data.total,
    payment_type: data.paymentMethod,
  });

  window.gtag('event', EVENT_NAMES.ADD_PAYMENT_INFO, {
    payment_method: data.paymentMethod === 'cod' ? 'наложен_платеж' : 'карта',
  });
}

/**
 * Track purchase completion
 */
export function trackPurchase(order: {
  orderId: string;
  total: number;
  shipping: number;
  items: Array<{
    id: string;
    name: string;
    category: string;
    price: number;
    quantity: number;
  }>;
  paymentMethod: string;
  shippingMethod: string;
}) {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('event', 'purchase', {
    transaction_id: order.orderId,
    value: order.total,
    currency: 'EUR',
    shipping: order.shipping,
    items: order.items.map((item) => ({
      item_id: item.id,
      item_name: item.name,
      item_category: item.category,
      price: item.price,
      quantity: item.quantity,
    })),
  });

  window.gtag('event', EVENT_NAMES.PURCHASE, {
    order_id: order.orderId,
    total: order.total,
    items_count: order.items.length,
    payment_method: order.paymentMethod === 'cod' ? 'наложен_платеж' : 'карта',
    shipping_method: order.shippingMethod,
  });
}

/**
 * Track designer events
 */
export function trackDesignerOpen(productId: string, productName: string) {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('event', EVENT_NAMES.OPEN_DESIGNER, {
    product_id: productId,
    product_name: productName,
  });
}

export function trackDesignerAddText() {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('event', EVENT_NAMES.ADD_TEXT);
}

export function trackDesignerUploadImage() {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('event', EVENT_NAMES.UPLOAD_IMAGE);
}

export function trackDesignerSaveDesign(productId: string) {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('event', EVENT_NAMES.SAVE_DESIGN, {
    product_id: productId,
  });
}

/**
 * Track search
 */
export function trackSearch(searchTerm: string, resultsCount: number) {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('event', 'search', {
    search_term: searchTerm,
  });

  window.gtag('event', EVENT_NAMES.SEARCH, {
    search_term: searchTerm,
    results_count: resultsCount,
  });
}

/**
 * Track category view
 */
export function trackCategoryView(categoryName: string, productCount: number) {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('event', EVENT_NAMES.VIEW_CATEGORY, {
    category_name: categoryName,
    product_count: productCount,
  });
}

/**
 * Track contact form submission
 */
export function trackContactForm(subject: string) {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('event', 'generate_lead', {
    source: 'contact_form',
  });

  window.gtag('event', EVENT_NAMES.CONTACT_FORM, {
    subject: subject,
  });
}

/**
 * Track social share
 */
export function trackShare(
  method: string,
  contentType: string,
  itemId: string
) {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('event', 'share', {
    method: method,
    content_type: contentType,
    item_id: itemId,
  });

  window.gtag('event', EVENT_NAMES.SHARE, {
    platform: method,
    content_type: contentType,
  });
}

/**
 * Track click-to-call
 */
export function trackPhoneClick() {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('event', EVENT_NAMES.CLICK_PHONE);
}

/**
 * Track email click
 */
export function trackEmailClick() {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('event', EVENT_NAMES.CLICK_EMAIL);
}

/**
 * Track error events
 */
export function trackError(errorMessage: string, errorLocation: string) {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('event', 'exception', {
    description: errorMessage,
    fatal: false,
  });

  window.gtag('event', 'грешка', {
    error_message: errorMessage,
    error_location: errorLocation,
  });
}

/**
 * Set user properties
 */
export function setUserProperties(properties: {
  language?: string;
  isReturningCustomer?: boolean;
}) {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('set', 'user_properties', {
    preferred_language: properties.language || 'bg',
    is_returning_customer: properties.isReturningCustomer || false,
  });
}
